import API from "@/api";
import { getField, updateField } from "vuex-map-fields";
import moment from "moment";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import translation from "@/translation.json";

const initialState = () => ({
  loadingTable: false,
  loadingExport: false,
  numberOfUsersToExport: 1000,
  users: [],

  // paginacao
  offset: 0,

  // filtros
  searchBy: ["E-mail", "Login", "Nome"],
  perfilItems: [],
  segmentacaoItems: [],
  dominioItems: [],
  currentDominio: null,
  selectedGroup: null,
  selectedMunicipio: null,
  selectedRegiaoSaude: null,
  searchText: null,
  selectedSearchBy: null,
  selectedStatus: null,
  providers: ["SSO", "Outro"],
  selectedProvider: null,
  statusList: ["Habilitados", "Desabilitados"],

  usersImportResponse: null,
});

export const state = initialState();

export const getters = {
  getField,
  formatDate: () => (date) => formatDate(date),
  formatActions: () => (actions) => formatActions(actions),
  // totalPages: () => state => totalPages(state)
};

function mountSearchPayload(state, limit) {
  const config = {
    params: new URLSearchParams(),
  };
  config.params.append("dominio", state.currentDominio);

  if (state.selectedGroup)
    config.params.append("idPerfil", state.selectedGroup);

  let attrs = {};
  if (state.selectedRegiaoSaude && !state.selectedMunicipio) {
    attrs = JSON.stringify({ nmRegiaoSaude: state.selectedRegiaoSaude });
    config.params.append("attributes", attrs);
  }

  if (state.selectedMunicipio) {
    attrs = JSON.stringify(state.selectedMunicipio);
    config.params.append("attributes", attrs);
  }

  if (state.searchText) {
    if (state.selectedSearchBy === "Login") {
      config.params.append("username", state.searchText);
    } else if (state.selectedSearchBy === "E-mail") {
      config.params.append("email", state.searchText);
    } else if (state.selectedSearchBy === "Nome") {
      config.params.append("firstName", state.searchText);
    }
  }

  if (state.selectedProvider)
    config.params.append("origem", state.selectedProvider);

  if (state.selectedStatus)
    config.params.append("enabled", state.selectedStatus === "Habilitados");

  if (!limit) {
    config.params.append("offset", state.offset);
    config.params.append("limit", state.itemsPerPage);
  } else {
    config.params.append("offset", 0);
    config.params.append("limit", limit);
  }
  return config;
}

export const actions = {
  async getPerfis({ commit }) {
    const response = await API.axios.get("/sso/api/v1/perfis");
    if (response.status === 200) {
      commit("updateField", { path: "perfilItems", value: response.data });
    }
    return response.status ? response.status : undefined;
  },
  async getSegmentacoes({ commit }) {
    const response = await API.axios.get("/sso/api/v1/segmentacoes");
    if (response?.status === 200) {
      commit("updateField", { path: "segmentacaoItems", value: response.data });
    }
    return response?.status ?? undefined;
  },
  async getDominios({ commit }) {
    const response = await API.axios.get("/sso/api/v1/usuarios/dominio");
    if (response.status === 200) {
      commit("updateField", { path: "dominioItems", value: response.data });
    }
    return response.status ? response.status : undefined;
  },
  async get({ state, commit }) {
    commit("updateField", { path: "loadingTable", value: true });
    commit("updateField", { path: "currentPage", value: 1 });
    commit("updateField", { path: "totalPages", value: 0 });

    const result = await API.axios.get(
      "sso/api/v1/usuarios",
      mountSearchPayload(state, 2000)
    );

    if (!result || (result.status >= 400 && result.status < 600)) {
      commit("updateField", { path: "loadingTable", value: false });
      return !result.data.message ? undefined : result.data.message;
    }

    commit("updateField", { path: "offset", value: result.data.offset });
    commit("updateField", {
      path: "totalItems",
      value: result.data.totalElements,
    });
    const users = reorderResult(result.data.content);

    commit("updateField", { path: "users", value: users });
    commit("updateField", { path: "loadingTable", value: false });
    return state.users;
  },

  async importUsers({ commit }, file) {
    var payload = { file: file };
    const response = await API.axios.post(
      "/sso/api/v1/usuarios/import",
      payload
    );
    commit("updateField", {
      path: "usersImportResponse",
      value: response.data,
    });
    return response;
  },
  // eslint-disable-next-line no-unused-vars
  async postUser({ dispatch, state }, payload) {
    const config = {
      params: new URLSearchParams(),
    };
    config.params.append("dominio", state.currentDominio);
    const response = await API.axios.post(
      "/sso/api/v1/usuarios",
      payload,
      config
    );
    return response;
  },
  // eslint-disable-next-line no-unused-vars
  async putUser({ dispatch, state }, payload, dominio) {
    const config = {
      params: new URLSearchParams(),
    };
    config.params.append("dominio", state.currentDominio);

    const response = await API.axios.put(
      `/sso/api/v1/usuarios/${payload.id}`,
      payload,
      config
    );

    return response;
  },
  // eslint-disable-next-line no-unused-vars
  async deleteUser({ dispatch, state }, id) {
    const config = {
      params: new URLSearchParams(),
    };
    config.params.append("dominio", state.currentDominio);
    const response = await API.axios.delete(
      `/sso/api/v1/usuarios/${id}`,
      config
    );
    return response;
  },
  // eslint-disable-next-line no-unused-vars
  async exportUserListAsXlsx(
    { dispatch, state, commit, rootState },
    { exportRegiaoSaude, exportMunicipio }
  ) {
    let date = moment().format("YYYY-MM-DD");
    commit("updateField", { path: "loadingExport", value: true });
    commit("updateField", { path: "messageSuccess", value: "" });

    if (state.paginationToken != null) {
      commit("updateField", { path: "snackbarSuccess", value: true });
      commit("updateField", {
        path: "messageSuccess",
        value: "Carregando dados...",
      });
    }

    // while (state.paginationToken != null) {
    //   await dispatch('getMoreUsers')
    // }

    commit("updateField", { path: "snackbarSuccess", value: true });
    commit("updateField", {
      path: "messageSuccess",
      value: "Gerando arquivo...",
    });

    const workbook = new ExcelJS.Workbook();
    const worksheetUsers = workbook.addWorksheet("Usuários");
    const worksheetFiltros = workbook.addWorksheet(
      "Filtros Utilizados na Busca"
    );

    worksheetFiltros.columns = [
      { header: "Tipo Busca por", key: "searchBy", width: 12 },
      { header: "Valor Busca por", key: "value", width: 13 },
      { header: "Perfil", key: "perfil", width: 12 },
      { header: "Habilitado", key: "enabledStatus", width: 12 },
      { header: "Região de Saúde", key: "nmRegiaoSaude", width: 30 },
      { header: "Município", key: "nmMunicipio", width: 30 },
    ];

    let usersColumns = [
      { header: "Região de Saúde", key: "nmRegiaoSaude", width: 20 },
      { header: "Município", key: "nmMunicipio", width: 20 },
      { header: "Login", key: "username", width: 25 },
      { header: "Nome", key: "name", width: 25 },
      { header: "E-mail", key: "email", width: 27 },
      { header: "Autenticação", key: "autenticacao", width: 16 },
      { header: "Status", key: "status", width: 12 },
      { header: "Perfil", key: "perfil", width: 13 },
      { header: "Habilitado", key: "habilitado", width: 14 },
      { header: "Data Criação", key: "dtCriacao", width: 19 },
      { header: "Último Acesso", key: "dtUltimoAcesso", width: 19 },
      { header: "Segmentação", key: "segmentation", width: 14 },
    ];

    worksheetUsers.columns = usersColumns;

    // Add autofilter on each column
    worksheetUsers.autoFilter = "C1:F1";
    worksheetFiltros.autoFilter = "A1:D1";

    const result = await API.axios.get(
      "sso/api/v1/usuarios",
      mountSearchPayload(state, state.numberOfUsersToExport)
    );
    if (!result || (result.status >= 400 && result.status < 600)) {
      commit("updateField", { path: "loadingTable", value: false });
      return !result.data.message ? undefined : result.data.message;
    }

    let users = reorderResult(result.data.content);

    for (var i = 0; i < users.length; i++) {
      var user = users[i];

      let row = {
        nmRegiaoSaude: user.attributes?.nmRegiaoSaude
          ? user.attributes?.nmRegiaoSaude[0]
          : "-",
        nmMunicipio: user.attributes?.nmMunicipio
          ? user.attributes?.nmMunicipio[0]
          : "-",
        username: user.username,
        name: user.name,
        email: user.email,
        autenticacao: user.federatedIdentities
          ? user.federatedIdentities[0]
            ? user.federatedIdentities[0].identityProvider
            : ""
          : "",
        status: formatActions(user.requiredActions),
        perfil: getPerfilNameById(
          state,
          user.attributes?.idPerfil ? user.attributes?.idPerfil[0] : null
        ),
        habilitado: user.enabled ? "Sim" : "Não",
        dtCriacao: formatDate(new Date(user.createdTimestamp)),
        dtUltimoAcesso: formatDate(
          user.attributes?.lastLogin
            ? new Date(user.attributes?.lastLogin)
            : null
        ),
        segmentation: getSegmentacaoNameById(
          state,
          user.attributes?.idSegmentacao
            ? user.attributes?.idSegmentacao[0]
            : null
        ),
      };

      worksheetUsers.addRow(row);
    }

    let worksheetFiltrosRows = {
      searchBy: state.selectedSearchBy,
      value: state.searchText,
      perfil: getPerfilNameById(state, state.selectedGroup),
      enabledStatus: state.selectedStatus,
    };

    if (exportRegiaoSaude) {
      worksheetFiltrosRows = {
        ...worksheetFiltrosRows,
        nmRegiaoSaude: state.selectedRegiaoSaude?.nmRegiaoSaude
          ? state.selectedRegiaoSaude?.nmRegiaoSaude
          : "-",
      };
    }

    if (exportMunicipio) {
      worksheetFiltrosRows = {
        ...worksheetFiltrosRows,
        nmMunicipio: state.selectedMunicipio?.nmMunicipio
          ? state.selectedMunicipio?.nmMunicipio
          : "-",
      };
    }
    worksheetFiltros.addRow(worksheetFiltrosRows);

    formatSheetStyle(worksheetFiltros);
    formatSheetStyle(worksheetUsers);

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], { type: "application/octet-stream" });
      FileSaver.saveAs(blob, `SMAIB_Export_Usuarios_${date}.xlsx`);
    });

    commit("updateField", { path: "snackbarSuccess", value: false });
    commit("updateField", { path: "loadingExport", value: false });
  },

  resetState({ commit }) {
    commit("resetState");
  },

  async updateVersaoAceiteTermos({ commit }) {
    const result = await API.axios.put("/sso/api/v1/usuarios/aceite-termos");

    if (result.status === 204) {
      commit("updateField", { path: "loadingTable", value: false });
      return result;
    } else {
      throw new Error(
        `Erro ao atualizar aceitação dos termos. Status: ${result.status}`
      );
    }
  },
};

export const mutations = {
  updateField,
  resetState(state) {
    Object.assign(state, initialState());
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

function reorderResult(result) {
  const reorderedResult = result
    .map((user) => {
      return formatUserInfo(user);
    })
    .sort((a, b) => {
      const getSortWeight = (obj) => {
        const hasRegiao =
          obj.attributes?.nmRegiaoSaude &&
          obj.attributes?.nmRegiaoSaude[0] !== "-";
        const hasMunicipio =
          obj.attributes?.nmMunicipio && obj.attributes?.nmMunicipio[0] !== "-";

        if (hasRegiao && hasMunicipio) return 1;
        if (hasRegiao) return 2;
        if (hasMunicipio) return 3;
        return 4;
      };

      const weightA = getSortWeight(a);
      const weightB = getSortWeight(b);

      if (weightA !== weightB) {
        return weightA - weightB;
      }

      const regiaoA = a.attributes?.nmRegiaoSaude?.[0] || "";
      const regiaoB = b.attributes?.nmRegiaoSaude?.[0] || "";

      if (regiaoA !== regiaoB) return regiaoA.localeCompare(regiaoB);

      const municipioA = a.attributes?.nmMunicipio?.[0] || "";
      const municipioB = b.attributes?.nmMunicipio?.[0] || "";

      if (municipioA !== municipioB)
        return municipioA.localeCompare(municipioB);

      return a.name.localeCompare(b.name);
    });
  return reorderedResult;
}

function formatUserInfo(user) {
  const regiaoSaude = user.attributes?.nmRegiaoSaude;
  const municipio = user.attributes?.municipio;
  const firstName = user.firstName ?? "";
  const lastName = user.lastName ?? "";

  if (regiaoSaude?.[0] === "") {
    regiaoSaude[0] = "-";
  }
  if (municipio?.[0] === "") {
    municipio[0] = "-";
  }

  const name = `${firstName} ${lastName}`;

  return {
    ...user,
    name,
  };
}

function formatDate(data) {
  return data == null ? "-" : moment(data).format("DD/MM/YYYY HH:mm");
}

function formatActions(actions) {
  if (actions.length === 0) return "Confirmado";
  // if (translation.userStatus[actions]) { return translation.userStatus[actions].description } else return actions
  var actionsFormatted = "";
  for (var i = 0; i < actions.length; i++) {
    actionsFormatted += translation.userStatus[actions[i]].description;
    if (i < actions.length - 1) {
      actionsFormatted += ", ";
    }
  }
  return actionsFormatted;
}

// function totalPages (state) {
//   return Math.ceil(state.totalItems / state.itemsPerPage)
// }

function formatSheetStyle(worksheet) {
  // Process each row for beautification
  worksheet.eachRow(function (row, rowNumber) {
    row.eachCell((cell) => {
      if (rowNumber === 1) {
        // First set the background of header row
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "005197" },
        };
        cell.font = {
          family: 4,
          size: 12,
          bold: true,
          color: { argb: "ffffff" },
        };
      }
      // Set border of each cell
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });
    // Commit the changed row to the stream
    row.commit();
  });
}

function getPerfilNameById(state, id) {
  if (id) {
    const group = state.perfilItems.find((group) => group.id === id);
    if (group) return group.name;
  }
  return "-";
}

function getSegmentacaoNameById(state, id) {
  if (id) {
    const group = state.segmentacaoItems.find((group) => group.id === id);
    if (group) return group.name;
  }
  return "-";
}
